/**
 * Electricity price data types for Swiss electricity tariffs
 * Data sourced from ElCom (Federal Electricity Commission)
 * https://www.elcom.admin.ch/en/electricity-prices-and-tariffs
 */

// Tariff categories for households (H1-H8) and commercial (C1-C7)
export type ElectricityCategory =
  | 'H1'
  | 'H2'
  | 'H3'
  | 'H4'
  | 'H5'
  | 'H6'
  | 'H7'
  | 'H8'
  | 'C1'
  | 'C2'
  | 'C3'
  | 'C4'
  | 'C5'
  | 'C6'
  | 'C7'

// Price components breakdown
export interface ElectricityPrices {
  price_total: number // Total price in Rp./kWh
  price_distribution: number // Network distribution cost
  price_energy: number // Energy cost
  price_taxes: number // Local/cantonal taxes
  price_supplement: number // Federal supplement (KEV)
}

// Yearly historical data for an operator
export interface ElectricityYearlyData {
  year: number
  prices: ElectricityPrices
}

// Individual electricity grid operator data
export interface ElectricityGridOperator {
  grid_operator_number: number
  grid_operator_name: string
  prices?: ElectricityPrices
  yearly_data?: ElectricityYearlyData[]
  total_price_evolution_1_year?: number
  total_price_evolution_2_year?: number
  total_price_evolution_5_year?: number
  total_price_evolution_10_year?: number
}

// Main electricity price data structure from Elasticsearch
export interface ElectricityPriceData {
  // Location identifiers
  municipality_id: string
  municipality_name: string
  district_name: string
  region_name: string
  canton_name: string
  year: number
  category: ElectricityCategory

  // Price summary for this municipality/category
  total_price_average: number // Average price in Rp./kWh
  total_price_min: number // Cheapest operator
  total_price_max: number // Most expensive operator
  grid_operator_name_min: string // Name of cheapest grid operator
  grid_operator_name_max: string // Name of most expensive grid operator

  // Evolution metrics (percentage change)
  total_price_min_evolution_1_year?: number
  total_price_min_evolution_2_year?: number
  total_price_min_evolution_5_year?: number
  total_price_min_evolution_10_year?: number

  // All grid operators in this municipality for this category
  grid_operators?: ElectricityGridOperator[]
}

// Annual cost estimate based on consumption profile
export interface ElectricityCostEstimate {
  consumption_kwh: number
  cost_min: number // CHF/year with cheapest operator
  cost_max: number // CHF/year with most expensive operator
  cost_average: number // CHF/year average
}

// Category consumption profiles (kWh/year)
export const ELECTRICITY_CATEGORY_CONSUMPTION: Record<
  ElectricityCategory,
  number
> = {
  H1: 1600, // 2-room apt, electric cooker
  H2: 2500, // 4-room apt, electric cooker
  H3: 4500, // 4-room apt, cooker + water heater
  H4: 4500, // 5-room apt, cooker + tumble dryer
  H5: 7500, // 5-room house, all electric equipment
  H6: 25000, // 5-room house, resistance heating
  H7: 13000, // 5-room house, 5 kW heat pump
  H8: 7500, // Large apt, highly electrified
  C1: 8000, // Very small enterprise, max 8 kW
  C2: 30000, // Small enterprise, max 15 kW
  C3: 150000, // Medium enterprise, max 50 kW
  C4: 500000, // Large enterprise, 150 kW, low voltage
  C5: 500000, // Large enterprise, 150 kW, medium voltage
  C6: 1500000, // Large enterprise, 400 kW, medium voltage
  C7: 7500000, // Large enterprise, 1'630 kW, medium voltage
}

// Complete electricity analysis response
export interface ElectricityAnalysis {
  current_data: ElectricityPriceData
  annual_cost: ElectricityCostEstimate
  price_comparison?: {
    vs_canton_median: number
    vs_national_median: number
    canton_median_price: number
    national_median_price: number
  }
}

// Location lookup result for municipality
export interface MunicipalityLocation {
  id: number
  municipality_id: string
  municipality_name: string
  canton_id: string
  canton_name: string
  geo_center?: {
    lat: number
    lon: number
  }
}
